import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';
import { NgxMaskModule } from 'ngx-mask';
import { environment } from 'src/environments/environment';
import { ChangePasswordComponent } from '../changePassword/change-password/change-password.component';
import { constant } from '../constant/constant';
import { UserService } from '../services/User/user.service';
import { TokenStorageService } from '../services/utility/token-storage-service';
import { UtilityService } from '../services/utility/utility.service';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { MatCard } from "@angular/material/card";
import { ExcelDownloadStateService } from 'src/app/services/ReportServices/excelsignalr.service';

declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: () => void;
  }
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatRadioModule,
    FlexLayoutModule,
    NgxMaskModule,
    ZXingScannerModule
]
})

export class LoginComponent implements OnInit, OnDestroy {
  user: any = {};
  loggedIn = false;
  token: string | undefined;
  productAccess: any = "";
  ProductAccess: any = [];
  UserAccessMaster: any = {};
  dbVerifiedUser: any = {};
  recaptchaInvalid = false;
  recaptchaResponse: string | null = null;
  @ViewChild('recaptchaElement', { static: false }) recaptchaElement!: ElementRef;
  recaptchaWidgetId: number | null = null;

  constructor(
    private router: Router,
    private userService: UserService,
    private tokenService: TokenStorageService,
    private dialogService: MatDialog,
    private utilityService: UtilityService,
    private excelSignalRService: ExcelDownloadStateService
  ) {
    this.token = undefined;
  }

  hide: boolean = true;
  hideNewPassword: boolean = true;
  hideRetypePassword: boolean = true;
  isLoginView: boolean = true;
  isForgotPasswordView: boolean = false;
  isEnterOTPView: boolean = false;
  isSetNewPasswordView: boolean = false;

  // QR Scanner properties - USB/Bluetooth only
  isQrLoginView: boolean = false;
  qrScannerActive: boolean = false;
  qrResultString: string = '';
  scannedQrData: string = '';

  // Scan timer properties for USB/Bluetooth scanner
  scanInProgress: boolean = false;
  scanTimer: any = null;
  scanDebounceTime: number = 500;

  minScanLength: number = 20;
  scanStartTime: number = 0;
  scanElapsedTime: number = 0;

  // Keep ZXing imports for potential future use, but not actively using camera

  @ViewChild('qrInput', { static: false }) qrInput!: ElementRef<HTMLInputElement>;

  EnteredMobileForOTP: string = '';
  EnteredEmailForOTP: string = '';

  npiForVerifyOTP: string = '';
  resetPasswordToken: string = '';

  // Store registered email and mobile for validation
  registeredEmail: string = '';
  registeredMobileNo: string = '';

  // OTP digits array
  otpDigits = ['', '', '', '', '', ''];

  // Store masked email and mobile number for display
  maskedEmail: string = '';
  maskedMobile: string = '';

  // Countdown timer for Resend OTP
  otpCountdown: number = 120;
  otpCountdownDisplay: string = '02:00';
  isResendDisabled: boolean = true;
  private countdownInterval: any = null;

  // Password strength validation properties
  panelOpenState: boolean = false;
  hasMinLength: boolean = false;
  hasUpperCase: boolean = false;
  hasLowerCase: boolean = false;
  hasNumber: boolean = false;
  hasSpecialChar: boolean = false;
  strengthMessage: string = 'Weak Password';
  strengthColor: string = 'warn';
  strengthIcon: string = 'error';

  // Helper method to mask email
  private maskEmail(email: string): string {
    if (!email) return '';

    const parts = email.split('@');
    if (parts.length !== 2) return email;

    const localPart = parts[0];
    const domain = parts[1];

    // For 1-2 characters, mask all
    if (localPart.length <= 2) {
      const maskedPart = '*'.repeat(localPart.length);
      return `${maskedPart}@${domain}`;
    }

    // For 3+ characters, show first 3 and mask the rest
    const visiblePart = localPart.substring(0, 3);
    const maskedPart = '*'.repeat(localPart.length - 3);

    return `${visiblePart}${maskedPart}@${domain}`;
  }

  // Helper method to mask mobile number
  private maskMobile(mobile: string): string {
    if (!mobile) return '';

    const mobileDigits = mobile.replace(/\D/g, ''); // Remove non-digits

    if (mobileDigits.length < 4) return mobile;

    const lastFour = mobileDigits.slice(-4);

    return `*** *** ${lastFour}`;
  }

  ngOnInit(): void {
    this.loadRecaptchaScript();
  }

  loadRecaptchaScript(): void {
    if (window.grecaptcha) {
      // Script already loaded, render with delay to ensure DOM is ready
      setTimeout(() => this.renderRecaptcha(), 200);
      return;
    }

    window.onRecaptchaLoad = () => this.renderRecaptcha();

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  renderRecaptcha(): void {
    if (!this.recaptchaElement?.nativeElement) {
      setTimeout(() => this.renderRecaptcha(), 100);
      return;
    }

    if (this.recaptchaWidgetId !== null) {
      window.grecaptcha.reset(this.recaptchaWidgetId);
      return;
    }

    this.recaptchaWidgetId = window.grecaptcha.render(this.recaptchaElement.nativeElement, {
      sitekey: environment.recaptcha.siteKey,
      callback: (response: string) => {
        this.recaptchaResponse = response;
        this.recaptchaInvalid = false;
      },
      'expired-callback': () => {
        this.recaptchaResponse = null;
        this.recaptchaInvalid = true;
      },
      'error-callback': () => {
        this.recaptchaResponse = null;
        this.recaptchaInvalid = true;
      }
    });
  }

  myFunction() {
    this.hide = !this.hide;
  }

  loginUser() {

    let pseudoNameSpearator = "@";
    this.user.appId = [];
    this.user.appId.push("MPOSWeb");

    if (!this.recaptchaResponse) {
      this.recaptchaInvalid = true;
      this.utilityService.showAlert(
        constant.KEY_ERROR,
        constant.ALERT_FAILED_TITLE,
        "Please complete the reCAPTCHA verification"
      );
      return;
    }

    let userLoginInfo = {
      user: this.user,
      stationId: ""
    };
    this.userService.loginUser(userLoginInfo).subscribe({
      next: (response: any) => {
        try {
          var receivedUserData = JSON.parse(response.data);

          if (response.result === "SUCCESS") {
            receivedUserData.appId = constant.APP_ID;
            localStorage.removeItem('pendingExcelJobIds');
            this.excelSignalRService.clearAllPendingJobs();
            this.authUserStore(receivedUserData);
          } else {
            var errorData = receivedUserData?.Message || "Unknown error occurred";
            this.utilityService.showToast(errorData, 'error');
          }
        } catch (err) {
          console.error('Error parsing response:', err);
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "Invalid response format");
        }
      },
      error: (error: any) => {
        console.error('Login request failed:', error);
        let errorMessage = error?.error?.message || error?.message || "An unexpected error occurred";
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorMessage);
      }
    });
  }

  authUserStore(data: any) {
    this.tokenService.saveToken(data.authToken);
    this.tokenService.saveRefreshToken(data.user.refreshToken);
    this.tokenService.saveUser(data.user);

    if (data.changePasswordAtLogin) {
      const changePasswordRef = this.dialogService.open(ChangePasswordComponent, {
        width: '40%',
        disableClose: false,
        data: { headerTitle: "Change Password at login", userData: data }
      });
      changePasswordRef.afterClosed().subscribe(result => {
        location.reload();
        // // Show QR update notification after password change at login
        // this.utilityService.showAlert(
        //   constant.KEY_WARNING,
        //   constant.PASSWORD_CHANGED_QR_UPDATE_TITLE,
        //   constant.PASSWORD_CHANGED_QR_UPDATE_MSG
        // ).subscribe(() => {

        // });
      });
    }
    else {
      this.userService.authenticateUserStore(data).subscribe((storeResult: any) => {

        var receivedStoreData = JSON.parse(storeResult.data);

        if (storeResult.result == "SUCCESS") {

          data.storeDetails = receivedStoreData.storeDetails;
          sessionStorage.setItem(constant.KEY_STORE_DETAILS, JSON.stringify(receivedStoreData.storeDetails));
          sessionStorage.setItem(constant.KEY_STATION_DETAILS, JSON.stringify(receivedStoreData.StationDetail));

          this.authUserRights(data);

        } else if (storeResult.result == "FAILURE") {

          var errorData = receivedStoreData.Message;
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorData);
        }
        else {
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "Error while fetching the store data");
        }
      });
    }
  }

  authUserRights(data: any) {
    this.userService.authorizeUserRights(data).subscribe((rightsResult: any) => {
      var receivedRightData = JSON.parse(rightsResult.data);
      if (rightsResult.result == "SUCCESS") {
        if (this.validateWebRights(receivedRightData.userRights.webRights.webAdministrativeFunction)) {

          data.rights = receivedRightData.userRights;
          this.userService.saveUserSessionData(data)

          const storeDetails = JSON.parse(sessionStorage.getItem(constant.KEY_STORE_DETAILS) || 'null');

          if (!storeDetails || (!storeDetails.rxLink && !storeDetails.primeRxCloudURL)) {
            console.warn('No valid store details found, redirecting to /store');
            this.router.navigateByUrl('/store');
          }
          else {
            this.router.navigateByUrl('/dashboard');
          }
        } else {
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "web rights are not given for the user!");
        }
      } else if (rightsResult.result == "FAILURE") {
        var errorData = receivedRightData.Message;
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorData);
      }
      else {
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "Error while fetching the rights data");
      }
    });
  }

  validateWebRights(rights: any): boolean {
    if (!rights || typeof rights !== 'object') {
      return false;
    }

    return Object.values(rights).some((value) => {
      if (value === true) {
        return true;  // Found a `true` value
      } else if (typeof value === 'object' && value !== null) {
        return this.validateWebRights(value);  // Recursively check nested objects
      }
      return false;
    });
  }

  // OTP Input handling methods
  trackByIndex(index: number): number {
    return index;
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;

    // Keep only numbers
    const digit = input.value.replace(/\D/g, '').charAt(0) || '';

    this.otpDigits[index] = digit;
    input.value = digit;

    // Move forward ONLY when user typed (not when navigating)
    if (digit && index < this.otpDigits.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const key = event.key;

    if ((event.ctrlKey || event.metaKey) && key.toLowerCase() === 'v') {
      return;
    }
    event.preventDefault();


    const current = document.getElementById(`otp-${index}`) as HTMLInputElement;

    if (/^\d$/.test(key)) {
      this.otpDigits[index] = key;
      current.value = key;

      if (index < this.otpDigits.length - 1) {
        this.focusInput(index + 1);
      }
      return;
    }

    // Backspace
    if (key === 'Backspace') {
      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
        current.value = '';
      } else if (index > 0) {
        this.otpDigits[index - 1] = '';
        const prev = this.focusInput(index - 1);
        prev.value = '';
      }
      return;
    }

    //  Arrows
    if (key === 'ArrowLeft' && index > 0) {
      this.focusInput(index - 1);
      return;
    }

    if (key === 'ArrowRight' && index < this.otpDigits.length - 1) {
      this.focusInput(index + 1);
      return;
    }
  }

  focusInput(index: number): HTMLInputElement {
    const input = document.getElementById(`otp-${index}`) as HTMLInputElement;
    input?.focus();
    return input;
  }

  onOtpFocus(event: FocusEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    // Don't select - just place cursor at end
    setTimeout(() => {
      if (input.value) {
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 0);
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const paste = event.clipboardData?.getData('text') || '';
    const digits = paste.replace(/\D/g, '').slice(0, 6);

    digits.split('').forEach((d, i) => {
      this.otpDigits[i] = d;
      const input = document.getElementById(`otp-${i}`) as HTMLInputElement;
      if (input) input.value = d;
    });

    this.focusInput(Math.min(digits.length, 5));
  }


  getOtpValue(): string {
    return this.otpDigits.join('');
  }

  goToForgotPassword() {
    this.isLoginView = false;
    this.isForgotPasswordView = true;
    this.isEnterOTPView = false;
    this.isSetNewPasswordView = false;
  }

  goToLoginPage() {

    if (this.qrScannerActive) {
      this.stopQrScanning();
    }

    this.isLoginView = true;
    this.isForgotPasswordView = false;
    this.isEnterOTPView = false;
    this.isSetNewPasswordView = false;
    this.isQrLoginView = false;

    // Reset QR scanner state (USB/Bluetooth only)
    this.qrResultString = '';
    this.scannedQrData = '';

    //empty all fields
    this.user = {};
    this.npiForVerifyOTP = '';
    this.resetPasswordToken = '';
    this.EnteredEmailForOTP = '';
    this.EnteredMobileForOTP = '';
    this.registeredEmail = '';
    this.registeredMobileNo = '';
    this.maskedEmail = '';
    this.maskedMobile = '';
    this.otpDigits = ['', '', '', '', '', ''];
    this.recaptchaResponse = null;
    this.recaptchaInvalid = false;

    // Clear countdown timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.otpCountdown = 120;
    this.isResendDisabled = true;

    // Force re-render of reCAPTCHA after view switch
    setTimeout(() => {
      // Reset the widget ID to force a fresh render
      this.recaptchaWidgetId = null;
      this.renderRecaptcha();
    }, 100);
  }


  //API calling for forgot password
  forgotPassword() {
    if (!this.user.userName) {
      this.utilityService.showToast("Please enter user name", 'error');
      return;
    }

    const userData = {
      userName: this.user.userName,
      email: null,
      mobileNumber: null
      // Add other required fields if needed
    };


    this.userService.forgotPassword(userData).subscribe({
      next: (response: any) => {
        try {
          if (response.result === "SUCCESS" && response.data && response.data !== 'null') {
            // Parse the data
            const receivedData = JSON.parse(response.data);

            // Check if parsed data is valid
            if (receivedData && receivedData.userEmail) {
              // Store registered email and mobile for validation
              this.registeredEmail = receivedData.userEmail || '';
              this.registeredMobileNo = receivedData.userMobileNumber || '';

              // Mask and store email and mobile number
              this.maskedEmail = this.maskEmail(receivedData.userEmail || '');
              this.maskedMobile = this.maskMobile(receivedData.userMobileNumber || '');

              // Navigate to forgot password screen to show masked values
              this.isLoginView = false;
              this.isForgotPasswordView = true;
              this.isEnterOTPView = false;
              this.isSetNewPasswordView = false;
            } else {
              // Data is null or invalid - show message from response
              const errorData =  `Email or Mobile Number not set for the user: ${this.user.userName}`;
              this.utilityService.showToast(errorData, 'error');
            }
          } else {
            // Handle failure or null data
            const errorData = response.message || "Unable to process request";
            this.utilityService.showToast(errorData, 'error');
          }
        } catch (err) {
          console.error('Error parsing response:', err);
          this.utilityService.showToast("Invalid response format", 'error');
        }
      },
      error: (error: any) => {
        console.error('Forgot password request failed:', error);
        let errorMessage = error?.error?.message || error?.message || "An unexpected error occurred";
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorMessage);
      }
    });
  }

  sendOTPCode() {
    const otpData = {
      userName: this.user.userName,
      email: this.EnteredEmailForOTP,
      mobileNumber: this.EnteredMobileForOTP
    };

    if (!this.user.userName) {
      this.utilityService.showToast("User name is required", 'error');
      return;
    }

    if (!this.EnteredEmailForOTP && !this.EnteredMobileForOTP) {
      this.utilityService.showToast("Please provide email address and mobile number", 'error');
      return;
    }

    if (!this.EnteredEmailForOTP) {
      this.utilityService.showToast("Please provide email address", 'error');
      return;
    }

    if (!this.EnteredMobileForOTP) {
      this.utilityService.showToast("Please provide mobile number", 'error');
      return;
    }

    // Validate if both email and mobile match registered values (case-insensitive for email)
    const emailMatches = this.EnteredEmailForOTP.toLowerCase() === this.registeredEmail.toLowerCase();
    const mobileMatches = this.EnteredMobileForOTP === this.registeredMobileNo;

    if (!emailMatches && !mobileMatches) {
      this.utilityService.showToast("Email and Mobile Number do not match registered details", 'error');
      return;
    }

    // Validate if entered email matches registered email
    if (!emailMatches) {
      this.utilityService.showToast("Please enter Registered Email ID.", 'error');
      return;
    }

    // Validate if entered mobile matches registered mobile
    if (!mobileMatches) {
      this.utilityService.showToast("Please enter Registered Mobile Number.", 'error');
      return;
    }

    this.userService.forgotPasswordWithoutRetry(otpData).subscribe({
      next: (response: any) => {
        try {
          if (response.result === "SUCCESS" && response.data) {
            const parsedData = JSON.parse(response.data);

            // Check if OTP was actually sent successfully
            // StatusCode should be 0 and RequestID should exist
            if (parsedData.StatusCode === 0 && parsedData.RequestID) {
              this.npiForVerifyOTP = parsedData.NPI || '';

              // Navigate to OTP entry screen
              this.isLoginView = false;
              this.isForgotPasswordView = false;
              this.isEnterOTPView = true;
              this.isSetNewPasswordView = false;

              // Reset OTP digits
              this.otpDigits = ['', '', '', '', '', ''];

              // Start countdown timer
              this.startOTPCountdown();

            } else if (parsedData.NPI && parsedData.expiresAt) {
              // OTP already exists and is not expired
              this.npiForVerifyOTP = parsedData.NPI || '';

              // Calculate remaining time from expiresAt timestamp
              const expiresAt = new Date(parsedData.expiresAt).getTime();
              const now = new Date().getTime();
              const remainingMs = expiresAt - now;
              const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

              // Show toast message
              this.utilityService.showToast(response.message || "Reset code already sent. Please check your email.", 'success');

              // Navigate to OTP entry screen
              this.isLoginView = false;
              this.isForgotPasswordView = false;
              this.isEnterOTPView = true;
              this.isSetNewPasswordView = false;

              // Reset OTP digits
              this.otpDigits = ['', '', '', '', '', ''];

              // Start countdown timer with actual remaining time
              this.startOTPCountdown(remainingSeconds);

            } else {
              // OTP was not sent - show the message from response
              const errorData = response.message || parsedData.StatusDescription || "Unable to send OTP";
              this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorData);
            }
          } else {
            const errorData = response.message || "Unable to send OTP";
            this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorData);
          }
        } catch (err) {
          console.error('Error processing OTP response:', err);
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "Invalid response format");
        }
      },
      error: (error: any) => {
        console.error('Send OTP request failed:', error);
        let errorMessage = error?.error?.message || error?.message || "Failed to send OTP";
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, constant.SOMETHING_WENT_WRONG);
      }
    });
  }

  resendOTP() {
    // Check if resend is disabled (timer still running)
    if (this.isResendDisabled) {
      return;
    }

    // Simply call sendOTPCode again
    this.sendOTPCode();
  }

  startOTPCountdown(initialSeconds: number = 120) {
    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Set countdown to the provided initial seconds
    this.otpCountdown = initialSeconds;
    this.isResendDisabled = true;
    this.updateCountdownDisplay();

    // Start countdown
    this.countdownInterval = setInterval(() => {
      if (this.otpCountdown > 0) {
        this.otpCountdown--;
        this.updateCountdownDisplay();
      }

      if (this.otpCountdown <= 0) {
        this.isResendDisabled = false;
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }, 1000);
  }

  updateCountdownDisplay() {
    const minutes = Math.floor(this.otpCountdown / 60);
    const seconds = this.otpCountdown % 60;
    this.otpCountdownDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  verifyOTP() {
    const otpData = {
      userName: this.user.userName,
      NPINo: this.npiForVerifyOTP,
      email: this.EnteredEmailForOTP,
      resetCode: this.getOtpValue()
    };

    if (!otpData.resetCode || otpData.resetCode.length !== 6) {
      this.utilityService.showToast("Please enter complete 6-digit OTP", 'error');
      return;
    }

    this.userService.verifyOTP(otpData).subscribe({
      next: (response: any) => {
        try {
          // Success case: response has result SUCCESS and data with token
          if (response.result === "SUCCESS" && response.data) {
            const parsedData = JSON.parse(response.data);

            if (parsedData.token) {
              // Store the token for password reset
              this.resetPasswordToken = parsedData.token;

              // Navigate to Set New Password screen
              this.isLoginView = false;
              this.isForgotPasswordView = false;
              this.isEnterOTPView = false;
              this.isSetNewPasswordView = true;
            } else {
              this.utilityService.showToast("Failed to verify OTP", 'error');
            }
          }
          // Failure case: response contains result and message
          else if (response.result === "FAILURE" || response.result === "ERROR") {
            const errorData = response.message || "Invalid OTP";
            this.utilityService.showToast(errorData, 'error');

            // Clear OTP fields for security and UX
            this.otpDigits = ['', '', '', '', '', ''];

            // Also clear the DOM inputs explicitly
            setTimeout(() => {
              for (let i = 0; i < 6; i++) {
                const input = document.getElementById(`otp-${i}`) as HTMLInputElement;
                if (input) {
                  input.value = '';
                }
              }
              // Focus back to first field
              const firstInput = document.getElementById('otp-0') as HTMLInputElement;
              if (firstInput) {
                firstInput.focus();
              }
            }, 100);
          }
          // Unexpected response format
          else {
            this.utilityService.showToast("Failed to verify OTP", 'error');
            // Clear OTP on error
            this.otpDigits = ['', '', '', '', '', ''];
          }
        } catch (err) {
          console.error('Error processing OTP verification response:', err);
          this.utilityService.showToast("Invalid response format", 'error');
          // Clear OTP on error
          this.otpDigits = ['', '', '', '', '', ''];
        }
      },
      error: (error: any) => {
        console.error('Verify OTP request failed:', error);
        let errorMessage = error?.error?.message || error?.message || "Failed to verify OTP";
        this.utilityService.showToast(errorMessage, 'error');

        // Clear OTP on error
        this.otpDigits = ['', '', '', '', '', ''];

      }
    });
  }

  resetForgotPassword() {

    const resetData = {
      UserName: this.user.userName,
      NewPassword: this.user.newPassword
    };

    if (!resetData.NewPassword) {
      this.utilityService.showToast("Please enter new password", 'error');
      return;
    }

    if (!this.user.retypePassword) {
      this.utilityService.showToast("Please retype password", 'error');
      return;
    }

    if (resetData.NewPassword !== this.user.retypePassword) {
      this.utilityService.showToast("Passwords do not match", 'error');
      return;
    }

    if (!this.resetPasswordToken) {
      this.utilityService.showToast("Session expired. Please restart the password reset process.", 'error');
      return;
    }

    this.userService.resetForgotPassword(resetData, this.resetPasswordToken).subscribe({
      next: (response: any) => {
        try {
          if (response.result === "SUCCESS") {
            this.resetPasswordToken = '';
            this.utilityService.showAlert(constant.KEY_SUCCESS, constant.ALERT_SUCCESS_TITLE, "Password reset successfully.").subscribe(() => {
                  // Navigate back to login page after alert is closed
                  this.goToLoginPage();
            });
          } else {
            const errorData = response.message || "Failed to reset password";
            this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorData);
          }
        } catch (err) {
          console.error('Error processing password reset response:', err);
          this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, "Invalid response format");
        }
      },
      error: (error: any) => {
        console.error('Reset password request failed:', error);
        let errorMessage = error?.error?.message || error?.message || "Failed to reset password";
        this.utilityService.showAlert(constant.KEY_ERROR, constant.ALERT_FAILED_TITLE, errorMessage);
      }
    });
  }



  // Password strength validation method
  checkPasswordStrength(password: string): void {
    if (!password) {
      this.hasMinLength = false;
      this.hasUpperCase = false;
      this.hasLowerCase = false;
      this.hasNumber = false;
      this.hasSpecialChar = false;
      this.strengthMessage = 'Weak Password';
      this.strengthColor = 'warn';
      this.strengthIcon = 'error';
      return;
    }

    this.hasMinLength = password.length >= 8;
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasLowerCase = /[a-z]/.test(password);
    this.hasNumber = /[0-9]/.test(password);
    this.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const strengthCount = [
      this.hasMinLength,
      this.hasUpperCase,
      this.hasLowerCase,
      this.hasNumber,
      this.hasSpecialChar
    ].filter(Boolean).length;

    if (strengthCount === 5) {
      this.strengthMessage = 'Strong Password';
      this.strengthColor = 'primary';
      this.strengthIcon = 'check_circle';
    } else if (strengthCount >= 3) {
      this.strengthMessage = 'Medium Password';
      this.strengthColor = 'accent';
      this.strengthIcon = 'warning';
    } else {
      this.strengthMessage = 'Weak Password';
      this.strengthColor = 'warn';
      this.strengthIcon = 'error';
    }
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
    }
    if (this.qrScannerActive) {
      this.stopQrScanning();
    }
  }

  // QR Scanner methods
  showQrLogin() {

    this.isLoginView = false;
    this.isForgotPasswordView = false;
    this.isEnterOTPView = false;
    this.isSetNewPasswordView = false;
    this.isQrLoginView = true;

    // Auto-start scanner when QR view opens
    setTimeout(() => {
      this.startQrScanning();
    }, 300);
  }

  startQrScanning() {
    this.qrScannerActive = true;
    this.scannedQrData = '';
    this.scanInProgress = false;
    this.scanElapsedTime = 0;

    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
      this.scanTimer = null;
    }

    // Focus on hidden input for USB/Bluetooth scanner (keyboard emulation)
    setTimeout(() => {
      if (this.qrInput?.nativeElement) {
        this.qrInput.nativeElement.focus();
      } else {
        console.error('QR input element not found!');
      }
    }, 100);
  }

  stopQrScanning() {
    this.qrScannerActive = false;
    this.scanInProgress = false;
    this.scanElapsedTime = 0;

    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
      this.scanTimer = null;
    }

  }

  // USB Scanner methods
  onQrInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Start scan timer if not already started
    if (!this.scanInProgress) {
      this.scanInProgress = true;
      this.scanStartTime = Date.now();
    }

    // Update elapsed time
    this.scanElapsedTime = Date.now() - this.scanStartTime;

    // Clear existing timer
    if (this.scanTimer) {
      clearTimeout(this.scanTimer);
    }

    // Set new timer - process after debounce time with no new input
    this.scanTimer = setTimeout(() => {
      const finalValue = input.value.trim();
      const totalScanTime = Date.now() - this.scanStartTime;

      // Validate minimum length
      if (finalValue.length >= this.minScanLength) {
        this.scanInProgress = false;
        this.processQrLogin(finalValue);
        input.value = ''; // Clear for next scan
      } else {
        this.utilityService.showToast('Incomplete QR scan. Please try again.', 'warning');
        this.scanInProgress = false;
        input.value = ''; // Clear incomplete scan
      }
    }, this.scanDebounceTime);
  }

  onQrInputBlur() {
    // Immediately refocus to keep input ready for next scan
    if (this.qrScannerActive && this.qrInput?.nativeElement) {
      setTimeout(() => {
        this.qrInput.nativeElement.focus();
      }, 10);
    }
  }

  processQrLogin(qrData: string) {
    try {
      // Input validation
      if (!qrData || qrData.trim().length === 0) {
        this.utilityService.showToast('Invalid QR code data', 'error');
        return;
      }

      const qrPayload = JSON.parse(qrData);

      // Validate structure
      if (!qrPayload || typeof qrPayload !== 'object') {
        this.utilityService.showToast('Invalid QR code format', 'error');
        return;
      }

      // Validate required fields
      if (!qrPayload.userName || typeof qrPayload.userName !== 'string') {
        this.utilityService.showToast('Invalid QR code');
        return;
      }

      if (!qrPayload.encryptedData || typeof qrPayload.encryptedData !== 'string') {
        console.error('Missing or invalid encryptedData');
        this.utilityService.showToast('Invalid QR code: missing encrypted data', 'error');
        return;
      }

      // Prepare login payload
      const userLoginInfo = {
        user: {
          userName: qrPayload.userName,
          password: '',
          appId: ['MPOSWeb']
        },
        stationId: '',
        isQRCodeLogin: true,
        qrData: qrPayload.encryptedData
      };

      // Call login API
      this.userService.loginUser(userLoginInfo).subscribe({
        next: (response: any) => {
          try {
            const receivedUserData = JSON.parse(response.data);

            if (response.result === 'SUCCESS') {
              receivedUserData.appId = constant.APP_ID;
              this.authUserStore(receivedUserData);
            } else {
              const errorData = receivedUserData?.Message || 'QR login failed';
              console.error('Login failed:', errorData);
              this.utilityService.showToast(errorData, 'error');
            }
          } catch (err) {
            console.error('Error parsing response:', err);
            this.utilityService.showAlert(
              constant.KEY_ERROR,
              constant.ALERT_FAILED_TITLE,
              'Invalid response format'
            );
          }
        },
        error: (error: any) => {
          console.error('📡 API REQUEST FAILED:', error);
          const errorMessage = error?.error?.message || error?.message || 'Failed to authenticate with QR code';
          this.utilityService.showToast(errorMessage, 'error');
        }
      });
    } catch (err) {
      console.error('ERROR PROCESSING QR CODE:', err);
      this.utilityService.showToast('Invalid QR code format', 'error');
    } finally {
      // Clear sensitive data from memory
      this.scannedQrData = '';
    }
  }
}
